import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { serverMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  getQuoteDeliveryRates,
  updateQuoteDeliveryRate,
  type DeliveryZoneId,
  type QuoteDeliveryRate,
} from "@/services/platformQuoteDeliveryRateService";

const QUERY_KEY = ["platform", "quote-delivery-rate"] as const;

/**
 * Pesos as typed, so a half-entered "27." does not snap to 27 mid-keystroke.
 *
 * At most two decimals, because the column is centésimos: accepting "270.555"
 * and storing 27056 would save an amount the admin never typed, silently.
 */
const PESOS_SHAPE = /^\d+(\.\d{1,2})?$/;

function isValidPesos(raw: string): boolean {
  const trimmed = raw.trim();
  if (!PESOS_SHAPE.test(trimmed)) return false;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 && n <= 10000;
}

/**
 * What Gestionalo charges to deliver the physical document an RFQ produces.
 *
 * A single value and its own card, beside the per-discipline fee rates rather
 * than inside them: a two-discipline RFQ is one package and pays ONE delivery
 * fee, so there is nothing to key this by. Putting it in that card's per-row
 * grid would draw a shipping cost per discipline that does not exist.
 *
 * Edited in PESOS and stored in centésimos. The admin thinks in the number
 * printed on a shipping invoice; the column is minor units like every other
 * amount on the platform.
 */
export function QuoteDeliveryRateCard(): JSX.Element {
  const { t } = useTranslation();

  const ratesQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getQuoteDeliveryRates,
  });

  return (
    <Card className="mt-4 shadow-sm">
      <CardContent className="p-6">
        <h2 className="text-sm font-semibold text-foreground">
          {t("platform.quoteDeliveryRate.title")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("platform.quoteDeliveryRate.hint")}{" "}
          {/* Read from the stored flag rather than asserted in the copy: the
              column exists precisely so the tax treatment can differ, and a
              hint that hardcodes "IVA included" would be a lie the moment it
              does. Taken from the first zone because the migration seeds both
              from the same value; if they ever diverge each row says so. */}
          {ratesQuery.data?.[0]
            ? t(
                ratesQuery.data[0].deliveryFeeIncludesIva
                  ? "platform.quoteDeliveryRate.ivaIncluded"
                  : "platform.quoteDeliveryRate.ivaExcluded",
              )
            : null}
        </p>

        {ratesQuery.isLoading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
            {t("platform.quoteDeliveryRate.loading")}
          </div>
        ) : ratesQuery.isError ? (
          <p className="mt-4 text-sm text-muted-foreground">
            {t("platform.quoteDeliveryRate.loadError")}
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            {(ratesQuery.data ?? []).map((rate) => (
              <ZoneRateRow key={rate.zone} rate={rate} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One zone's amount, with its OWN draft state and mutation.
 *
 * Per row rather than one draft lifted to the card, because two inputs sharing
 * a seeded draft is how an admin edits Montevideo and saves the interior
 * number. The seed-once ref that protects an in-progress edit from a
 * background refetch has to be per row for the same reason.
 */
function ZoneRateRow({ rate }: { rate: QuoteDeliveryRate }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    setDraft(String(rate.deliveryFeeMinor / 100));
  }, [rate.deliveryFeeMinor]);

  const saveMutation = useMutation({
    mutationFn: (payload: { zone: DeliveryZoneId; deliveryFeeMinor: number }) =>
      updateQuoteDeliveryRate(payload),
    onSuccess: async (saved) => {
      toast.success(t("platform.quoteDeliveryRate.saveSuccess"));
      setDraft(String(saved.deliveryFeeMinor / 100));
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: unknown) =>
      toast.error(serverMessage(err) ?? t("platform.quoteDeliveryRate.saveError")),
  });

  // Compared as centésimos, not as strings: "270.50" and "270.5" are the same
  // amount, and a string compare offered a save that would change nothing.
  const isDirty =
    isValidPesos(draft) && Math.round(Number(draft) * 100) !== rate.deliveryFeeMinor;
  const canSave = isDirty && !saveMutation.isPending && isValidPesos(draft);

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="w-48">
        <Label htmlFor={`quote-delivery-fee-${rate.zone}`}>
          {t(`platform.quoteDeliveryRate.zone_${rate.zone}`)}
        </Label>
        <Input
          id={`quote-delivery-fee-${rate.zone}`}
          type="number"
          min={0}
          max={10000}
          // Matches PESOS_SHAPE: step="1" made the browser treat a legitimate
          // "270.50" as invalid and fought the admin with its spinners.
          step="0.01"
          className="mt-1 tabular-nums"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      </div>
      <Button
        disabled={!canSave}
        onClick={() =>
          saveMutation.mutate({
            zone: rate.zone,
            deliveryFeeMinor: Math.round(Number(draft) * 100),
          })
        }
      >
        {saveMutation.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
        ) : null}
        {t("platform.quoteDeliveryRate.save")}
      </Button>
    </div>
  );
}
